import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AdminPageShellComponent } from '../../../shared/components/admin/admin-page-shell/admin-page-shell.component';
import { AdminStatCardComponent } from '../../../shared/components/admin/admin-stat-card/admin-stat-card.component';
import { CtaButtonComponent } from '../../../shared/components/ui/cta-button/cta-button.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { DailyFactsPaginationComponent } from '../daily-facts/components/daily-facts-pagination/daily-facts-pagination.component';
import { OnboardingStatusBadgeComponent } from './components/onboarding-status-badge/onboarding-status-badge.component';
import { UsersTableComponent } from './components/users-table/users-table.component';
import type {
  AdminUserDetail,
  AdminUserOnboardingStats,
  AdminUserSummary,
} from './models/admin-user.model';
import { UsersAdminService } from './services/users-admin.service';

type ActiveFilter = 'all' | 'active' | 'inactive';

interface FunnelStep {
  key: keyof AdminUserOnboardingStats;
  status: string;
  accent: 'neutral' | 'purple' | 'primary' | 'success';
}

const FUNNEL_STEPS: FunnelStep[] = [
  { key: 'statusCreated', status: 'CREATED', accent: 'neutral' },
  { key: 'statusStarted', status: 'STARTED', accent: 'purple' },
  { key: 'statusInfoSubmitted', status: 'INFO_SUBMITTED', accent: 'primary' },
  { key: 'statusFinalized', status: 'FINALIZED', accent: 'success' },
  { key: 'statusCompleted', status: 'COMPLETED', accent: 'success' },
];

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    TranslatePipe,
    MatProgressSpinnerModule,
    AdminPageShellComponent,
    AdminStatCardComponent,
    CtaButtonComponent,
    ModalComponent,
    DailyFactsPaginationComponent,
    OnboardingStatusBadgeComponent,
    UsersTableComponent,
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent {
  private readonly usersAdmin = inject(UsersAdminService);
  private readonly translate = inject(TranslateService);

  readonly funnelSteps = FUNNEL_STEPS;
  readonly roleOptions = [
    '',
    'USER',
    'PARENT',
    'TEACHER',
    'ADMIN',
    'PATROL',
    'PROFILER',
  ];
  readonly onboardingOptions = [
    '',
    'CREATED',
    'STARTED',
    'INFO_SUBMITTED',
    'FINALIZED',
    'COMPLETED',
  ];

  filterRole = '';
  filterOnboarding = '';
  filterSearch = '';
  filterActive: ActiveFilter = 'all';

  selectedUser = signal<AdminUserSummary | null>(null);
  userDetail = signal<AdminUserDetail | null>(null);
  detailLoading = signal(false);
  detailError = signal(false);

  readonly stats = this.usersAdmin.stats;
  readonly statsLoading = this.usersAdmin.statsLoading;
  readonly statsError = this.usersAdmin.statsError;
  readonly users = this.usersAdmin.users;
  readonly totalCount = this.usersAdmin.totalCount;
  readonly usersLoading = this.usersAdmin.usersLoading;
  readonly usersError = this.usersAdmin.usersError;
  readonly page = this.usersAdmin.page;
  readonly pageSize = this.usersAdmin.pageSize;

  readonly hasUsers = computed(() => this.users().length > 0);
  readonly maxFunnelCount = computed(() => {
    const stats = this.stats();
    if (!stats) {
      return 1;
    }
    return Math.max(
      stats.statusCreated,
      stats.statusStarted,
      stats.statusInfoSubmitted,
      stats.statusFinalized,
      stats.statusCompleted,
      1,
    );
  });

  applyFilters(): void {
    this.usersAdmin.applyFilters({
      role: this.filterRole,
      onboardingStatus: this.filterOnboarding,
      search: this.filterSearch,
      active: this.filterActive,
    });
  }

  resetFilters(): void {
    this.filterRole = '';
    this.filterOnboarding = '';
    this.filterSearch = '';
    this.filterActive = 'all';
    this.applyFilters();
  }

  refresh(): void {
    this.usersAdmin.refresh();
  }

  goToPage(page: number): void {
    this.usersAdmin.setPage(page);
  }

  funnelCount(key: keyof AdminUserOnboardingStats): number {
    const stats = this.stats();
    if (!stats) {
      return 0;
    }
    const value = stats[key];
    return typeof value === 'number' ? value : 0;
  }

  funnelWidth(key: keyof AdminUserOnboardingStats): number {
    return (this.funnelCount(key) / this.maxFunnelCount()) * 100;
  }

  filterByOnboardingStatus(status: string): void {
    this.filterOnboarding = status;
    this.applyFilters();
  }

  async openUserDetail(user: AdminUserSummary): Promise<void> {
    this.selectedUser.set(user);
    this.userDetail.set(null);
    this.detailError.set(false);
    this.detailLoading.set(true);

    try {
      this.userDetail.set(await this.usersAdmin.fetchUserDetail(user.id));
    } catch {
      this.detailError.set(true);
    } finally {
      this.detailLoading.set(false);
    }
  }

  closeUserDetail(): void {
    this.selectedUser.set(null);
    this.userDetail.set(null);
    this.detailError.set(false);
    this.detailLoading.set(false);
  }

  detailProgressLabel(detail: AdminUserDetail): string {
    return this.translate.instant('app.admin.users.detail.progress', {
      step: detail.summary.onboardingStep,
      percent: detail.summary.onboardingProgressPercent,
    });
  }
}
